import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Tpl3plService, Tpl3plConfig } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { InventoryService } from '../../../services/inventory.service';
import { OrdersService } from '../../../services/orders.service';

@Component({
	selector: 'app-tpl3pl-config-form',
	templateUrl: './tpl3pl-config-form.component.html',
	styleUrls: ['./tpl3pl-config-form.component.scss']
})
export class Tpl3plConfigFormComponent implements OnInit {
	configForm: UntypedFormGroup;
	config: Tpl3plConfig | null = null;
	loading = false;
	saving = false;
	testing = false;
	testResult: any = null;
	categories: any[] = [];
	productlines: any[] = [];
	brands: any[] = [];
	orderStatuses: any[] = [];

	constructor(
		private fb: UntypedFormBuilder,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private ordersService: OrdersService
	) {
		this.configForm = this.fb.group({
			enabled: [false],
			integration_type: ['API', Validators.required],
			warehouse_code: ['00', Validators.required],
			test_mode: [true],
			config_json: this.fb.group({
				api: this.fb.group({
					baseUrl: ['', [this.urlValidator()]],
					apiKey: [''],
					apiSecret: [''],
					timeout: [30, [Validators.min(1)]],
					retryAttempts: [3, [Validators.min(0)]]
				}),
				pgs: this.fb.group({
					baseUrl: ['', [this.urlValidator()]],
					userId: [''],
					passwd: [''],
					defaultWarehouse: ['CM'],
					defaultOutboundChannel: ['B2C'],
					b2cServiceType: ['Ground']
				}),
				edi: this.fb.group({
					tradingPartnerId: [''],
					isaQualifier: ['ZZ'],
					gsQualifier: ['01']
				}),
				sftp: this.fb.group({
					host: [''],
					port: [22, [this.portValidator()]],
					username: [''],
					password: [''],
					remotePath: ['/inbound'],
					outboundPath: ['/outbound']
				}),
				mapping: this.fb.group({
					orderStatus: this.fb.group({}),
					carrierMapping: this.fb.group({}),
					b2bB2c: this.fb.group({})
				}),
				allowed_statuses: [''],
				blocked_statuses: [''],
				shipped_statuses: [''],
				auto_invoice_on_shipment: [false],
				status_on_shipment_received: [null as any],
				status_on_tracking_in_transit: [null as any],
				status_on_tracking_delivered: [null as any],
				status_on_invoice_failed: [null as any],
				sku_categories: [[]],
				sku_lines: [[]],
				sku_manufacturers: [[]],
				syncSchedule: this.fb.group({
					inventory: ['*/15 * * * *'],
					shipments: ['*/5 * * * *']
				})
			})
		});
	}

	compareByManufacturerId = (a: any, b: any): boolean => Number(a) === Number(b);

	ngOnInit(): void {
		this.loadConfig();
		this.inventoryService.getItemCategories().subscribe((c: any) => { this.categories = c || []; });
		this.inventoryService.getItemProductlines().subscribe((p: any) => { this.productlines = p || []; });
		this.inventoryService.getBrands().subscribe((b: any) => { this.brands = b || []; });
		this.ordersService.getOrderStatuses().subscribe((r: any) => { this.orderStatuses = r || []; });
		this.configForm.get('integration_type')?.valueChanges.subscribe(() => {
			this.updateIntegrationValidators();
		});
		this.updateIntegrationValidators();
	}

	loadConfig(): void {
		this.loading = true;
		this.tpl3plService.getConfig().subscribe({
			next: (config) => {
				this.config = config;
				const cj = config.config_json || {};
				if (!cj.pgs) { cj.pgs = {}; }
				const arrToStr = (a: unknown) =>
					Array.isArray(a) ? a.map(String).join(', ') : (typeof a === 'string' ? a : '');
				const toArray = (a: unknown): unknown[] =>
					Array.isArray(a) ? [...a] : (typeof a === 'string' ? a.split(/[\s,]+/).map(s => s.trim()).filter(Boolean) : []);
				const toNumArray = (a: unknown): number[] =>
					Array.isArray(a) ? a.map((x: any) => Number(x)).filter((n: number) => !isNaN(n)) : [];
				this.configForm.patchValue({
					enabled: config.enabled,
					integration_type: config.integration_type,
					warehouse_code: config.warehouse_code,
					test_mode: config.test_mode,
					config_json: {
						...cj,
						allowed_statuses: arrToStr(cj.allowed_statuses),
						blocked_statuses: arrToStr(cj.blocked_statuses),
						shipped_statuses: arrToStr(cj.shipped_statuses),
						auto_invoice_on_shipment: !!cj.auto_invoice_on_shipment,
						status_on_shipment_received: cj.status_on_shipment_received != null && cj.status_on_shipment_received !== '' ? Number(cj.status_on_shipment_received) : null,
						status_on_tracking_in_transit: cj.status_on_tracking_in_transit != null && cj.status_on_tracking_in_transit !== '' ? Number(cj.status_on_tracking_in_transit) : null,
						status_on_tracking_delivered: cj.status_on_tracking_delivered != null && cj.status_on_tracking_delivered !== '' ? Number(cj.status_on_tracking_delivered) : null,
						status_on_invoice_failed: cj.status_on_invoice_failed != null && cj.status_on_invoice_failed !== '' ? Number(cj.status_on_invoice_failed) : null,
						sku_categories: toArray(cj.sku_categories),
						sku_lines: toArray(cj.sku_lines),
						sku_manufacturers: toNumArray(cj.sku_manufacturers)
					}
				});
				this.loading = false;
			},
			error: (error) => {
				this.globalSearchService.showNotification('Error loading configuration', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	private updateIntegrationValidators(): void {
		const integrationType = this.integrationType;
		const apiBaseUrl = this.configForm.get('config_json.api.baseUrl');
		const pgsBaseUrl = this.configForm.get('config_json.pgs.baseUrl');
		const pgsUserId = this.configForm.get('config_json.pgs.userId');
		const pgsPasswd = this.configForm.get('config_json.pgs.passwd');
		const ediPartnerId = this.configForm.get('config_json.edi.tradingPartnerId');
		const sftpHost = this.configForm.get('config_json.sftp.host');
		const sftpUser = this.configForm.get('config_json.sftp.username');
		const sftpPass = this.configForm.get('config_json.sftp.password');

		apiBaseUrl?.clearValidators();
		pgsBaseUrl?.clearValidators();
		pgsUserId?.clearValidators();
		pgsPasswd?.clearValidators();
		ediPartnerId?.clearValidators();
		sftpHost?.clearValidators();
		sftpUser?.clearValidators();
		sftpPass?.clearValidators();

		if (integrationType === 'API') {
			apiBaseUrl?.setValidators([Validators.required, this.urlValidator()]);
		}

		if (integrationType === 'PGS') {
			pgsBaseUrl?.setValidators([Validators.required, this.urlValidator()]);
			pgsUserId?.setValidators([Validators.required]);
			pgsPasswd?.setValidators([Validators.required]);
		}

		if (integrationType === 'EDI') {
			ediPartnerId?.setValidators([Validators.required]);
		}

		if (integrationType === 'SFTP' || integrationType === 'FTP') {
			sftpHost?.setValidators([Validators.required]);
			sftpUser?.setValidators([Validators.required]);
			sftpPass?.setValidators([Validators.required]);
		}

		apiBaseUrl?.updateValueAndValidity();
		pgsBaseUrl?.updateValueAndValidity();
		pgsUserId?.updateValueAndValidity();
		pgsPasswd?.updateValueAndValidity();
		ediPartnerId?.updateValueAndValidity();
		sftpHost?.updateValueAndValidity();
		sftpUser?.updateValueAndValidity();
		sftpPass?.updateValueAndValidity();
	}

	private urlValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			if (!value) {
				return null;
			}
			try {
				const parsed = new URL(value);
				if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
					return { url: true };
				}
				return null;
			} catch {
				return { url: true };
			}
		};
	}

	private portValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			if (value === null || value === undefined || value === '') {
				return null;
			}
			const port = Number(value);
			if (!Number.isInteger(port) || port < 1 || port > 65535) {
				return { port: true };
			}
			return null;
		};
	}

	saveConfig(): void {
		if (this.configForm.invalid) {
			this.globalSearchService.showNotification('Please fill in all required fields', 'warning', 'bottom', 'right');
			return;
		}

		const formValue = this.configForm.value;
		if (this.config?.enabled && !formValue.enabled) {
			const confirmed = window.confirm('Disable 3PL integration? This will stop exports and imports.');
			if (!confirmed) {
				return;
			}
		}

		this.saving = true;
		const cj = { ...formValue.config_json };
		const parseStatusIds = (s: string): number[] =>
			(s || '').split(/[\s,]+/).map((x: string) => parseInt(x.trim(), 10)).filter((n: number) => !isNaN(n));
		if (cj.allowed_statuses !== undefined) { cj.allowed_statuses = parseStatusIds(String(cj.allowed_statuses)); }
		if (cj.blocked_statuses !== undefined) { cj.blocked_statuses = parseStatusIds(String(cj.blocked_statuses)); }
		if (cj.shipped_statuses !== undefined) { cj.shipped_statuses = parseStatusIds(String(cj.shipped_statuses)); }
		const toStatusId = (v: any): number | null => { if (v === undefined || v === null || v === '') return null; const n = parseInt(String(v), 10); return isNaN(n) ? null : n; };
		if (cj.status_on_shipment_received !== undefined) { const v = toStatusId(cj.status_on_shipment_received); cj.status_on_shipment_received = v; }
		if (cj.status_on_tracking_in_transit !== undefined) { const v = toStatusId(cj.status_on_tracking_in_transit); cj.status_on_tracking_in_transit = v; }
		if (cj.status_on_tracking_delivered !== undefined) { const v = toStatusId(cj.status_on_tracking_delivered); cj.status_on_tracking_delivered = v; }
		if (cj.status_on_invoice_failed !== undefined) { const v = toStatusId(cj.status_on_invoice_failed); cj.status_on_invoice_failed = v; }
		const parseCsvStrings = (s: string): string[] =>
			(s || '').split(/[\s,]+/).map((x: string) => x.trim()).filter((v: string) => v.length > 0);
		if (Array.isArray(cj.sku_categories)) { /* keep */ } else if (cj.sku_categories !== undefined) { cj.sku_categories = parseCsvStrings(String(cj.sku_categories)); }
		if (Array.isArray(cj.sku_lines)) { /* keep */ } else if (cj.sku_lines !== undefined) { cj.sku_lines = parseCsvStrings(String(cj.sku_lines)); }
		if (Array.isArray(cj.sku_manufacturers)) {
			cj.sku_manufacturers = cj.sku_manufacturers.map((x: any) => Number(x)).filter((n: number) => !isNaN(n));
		} else if (cj.sku_manufacturers !== undefined) {
			cj.sku_manufacturers = parseCsvStrings(String(cj.sku_manufacturers)).map((x: string) => parseInt(x, 10)).filter((n: number) => !isNaN(n));
		}
		const config: Tpl3plConfig = {
			integration_type: formValue.integration_type,
			enabled: formValue.enabled,
			test_mode: formValue.test_mode,
			warehouse_code: formValue.warehouse_code,
			config_json: cj
		};

		this.tpl3plService.saveConfig(config).subscribe({
			next: (result: any) => {
				this.saving = false;
				if (result && result.success !== false) {
					this.globalSearchService.showNotification('Configuration saved successfully', 'success', 'bottom', 'right');
					this.loadConfig();
					this.tpl3plService.notifyConfigSaved();
				} else {
					const msg = (result && result.message) ? result.message : 'Save failed';
					this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				const msg = error?.error?.message || error?.message || 'Error saving configuration';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				this.saving = false;
			}
		});
	}

	testConnection(): void {
		this.testing = true;
		this.testResult = null;

		const formValue = this.configForm.value;
		const testConfig: Tpl3plConfig = {
			integration_type: formValue.integration_type,
			enabled: formValue.enabled,
			test_mode: formValue.test_mode,
			warehouse_code: formValue.warehouse_code,
			config_json: formValue.config_json
		};

		this.tpl3plService.testConnection(testConfig).subscribe({
			next: (result) => {
				this.testResult = result;
				this.testing = false;
				if (result.success) {
					this.globalSearchService.showNotification('Connection test successful', 'success', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification('Connection test failed: ' + result.message, 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.testResult = { success: false, message: 'Connection test error' };
				this.testing = false;
				this.globalSearchService.showNotification('Connection test error', 'danger', 'bottom', 'right');
			}
		});
	}

	get integrationType(): string {
		return this.configForm.get('integration_type')?.value || 'API';
	}

	get apiForm(): UntypedFormGroup {
		return this.configForm.get('config_json.api') as UntypedFormGroup;
	}

	get ediForm(): UntypedFormGroup {
		return this.configForm.get('config_json.edi') as UntypedFormGroup;
	}

	get sftpForm(): UntypedFormGroup {
		return this.configForm.get('config_json.sftp') as UntypedFormGroup;
	}

	get pgsForm(): UntypedFormGroup {
		return this.configForm.get('config_json.pgs') as UntypedFormGroup;
	}
}
